import { IconSparkles } from "@tabler/icons-react"
import { type FC } from "react"

import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"

import type { WebsiteAiEdit } from "./useWebsiteAiEdit"

type Props = { ai: WebsiteAiEdit; selectedPageId: number }

/** The strip above the preview that reviews an undecided proposal against what is on screen. */
const AiReviewBar: FC<Props> = ({ ai, selectedPageId }) => {
	const turnId = ai.undecidedTurn?.id ?? null
	if (turnId === null) return null

	const preview = ai.preview
	const page = preview?.pages.find(page => page.pageId === selectedPageId) ?? null
	const createdCount = page?.blocks?.filter(entry => entry.change === "created").length ?? 0
	const updatedCount = page?.blocks?.filter(entry => entry.change === "updated").length ?? 0
	const removedCount = page?.removedBlocks.length ?? 0
	const otherPageCount = preview?.pages.filter(page => page.pageId !== selectedPageId).length ?? 0

	const pageParts = [
		createdCount > 0 ? `${toCountLabel(createdCount, "section")} added` : null,
		updatedCount > 0 ? `${toCountLabel(updatedCount, "section")} rewritten` : null,
		removedCount > 0 ? `${toCountLabel(removedCount, "section")} removed` : null,
	].filter(part => part !== null)
	const summary =
		preview === null
			? "Preparing the preview of these changes..."
			: [
					pageParts.length > 0 ? `${pageParts.join(", ")} on this page` : "Nothing changes on this page",
					preview.style !== null ? "website style updated" : null,
					otherPageCount > 0 ? `${toCountLabel(otherPageCount, "other page")} changed` : null,
					preview.skippedOperationCount > 0
						? `${toCountLabel(preview.skippedOperationCount, "change")} no longer applies`
						: null,
				]
					.filter(part => part !== null)
					.join(" · ")

	return (
		<div className="border-primary/20 bg-primary/5 flex items-center justify-between gap-4 border-b px-6 py-2">
			<div className="flex min-w-0 items-center gap-2.5">
				<IconSparkles className="text-primary size-5 shrink-0" />
				<div className="min-w-0">
					<div className="text-sm font-semibold text-gray-900">Forento AI proposed changes</div>
					<div className="truncate text-xs text-gray-600">{summary}</div>
				</div>
			</div>
			<div className="flex shrink-0 items-center gap-2">
				{preview !== null && (
					<div className="flex rounded-lg border border-black/10 bg-white p-1">
						{[false, true].map(isProposed => (
							<button
								key={String(isProposed)}
								type="button"
								aria-pressed={ai.isPreviewVisible === isProposed}
								onClick={() => ai.setPreviewVisible(isProposed)}
								className={cn(
									"h-8 cursor-pointer rounded-md px-3 text-xs font-semibold transition-colors",
									ai.isPreviewVisible === isProposed
										? "bg-primary text-white"
										: "text-gray-500 hover:bg-gray-100 hover:text-gray-900",
								)}
							>
								{isProposed ? "With AI changes" : "Current"}
							</button>
						))}
					</div>
				)}
				<Button
					variant="secondary"
					className="h-10 rounded-lg px-3 py-2 text-sm"
					isDisabled={ai.applyingTurnId !== null || ai.discardingTurnId !== null}
					isLoading={ai.discardingTurnId === turnId}
					onClick={() => ai.discard(turnId)}
				>
					Discard
				</Button>
				<Button
					variant="primary"
					className="h-10 rounded-lg px-3 py-2 text-sm"
					isDisabled={ai.applyingTurnId !== null || ai.discardingTurnId !== null}
					isLoading={ai.applyingTurnId === turnId}
					onClick={() => ai.apply(turnId)}
				>
					Apply changes
				</Button>
			</div>
		</div>
	)
}

function toCountLabel(count: number, noun: string): string {
	return `${count} ${noun}${count === 1 ? "" : "s"}`
}

export default AiReviewBar
